import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

const STATUS_MAP = {
  DRAFT: {
    bg: '#FFF4D6',
    color: '#8A6D00',
    label: 'DRAFT:',
    description: 'Package created for preview, not yet delivered.',
  },
  CREATED: {
    bg: '#DBDEE0',
    color: '#112233',
    label: 'CREATED:',
    description: 'Initial state of package. Data collection may not be complete.',
  },
  ORPHANED: {
    bg: '#fdeaea',
    color: '#9c2222',
    label: 'ORPHANED:',
    description: 'Package whose Packager was deleted.',
  },
  PROCESSING: {
    bg: '#E0F1F7',
    color: '#32A0CB',
    label: 'PROCESSING:',
    description: 'Package is currently being processed.',
  },
  PROCESSING_SUCCESSFUL: {
    bg: '#e8f7e8',
    color: '#43a047',
    label: 'PROCESSING SUCCESSFUL:',
    description: 'Processing completed successfully.',
  },
  PROCESSING_FAILED: {
    bg: '#fdeaea',
    color: '#e53935',
    label: 'PROCESSING FAILED:',
    description: 'An error occurred during data collection or pre-processing.',
  },
  VERIFIED: {
    bg: '#e8f7e8',
    color: '#43a047',
    label: 'VERIFIED:',
    description: 'Package is validated and ready for delivery.',
  },
  FROZEN: {
    bg: '#9CA4B915',
    color: '#5F677B',
    label: 'FROZEN:',
    description: 'Package contents are locked and cannot be modified.',
  },
  TRANSMISSION_SUCCESSFUL: {
    bg: '#e8f7e8',
    color: '#43a047',
    label: 'TRANSMISSION SUCCESSFUL:',
    description: 'Successfully delivered.',
  },
  TRANSMISSION_FAILED: {
    bg: '#fdeaea',
    color: '#e53935',
    label: 'TRANSMISSION FAILED:',
    description: 'Delivery failed.',
  },
};

type PackageStatusProps = {
  status: keyof typeof STATUS_MAP;
  description?: string;
  onClick?: () => void;
};

const PackageStatus: React.FC<PackageStatusProps> = ({ status, description, onClick }) => {
  const statusInfo = STATUS_MAP[status];

  if (!statusInfo) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        '&:hover .edit-icon': {
          opacity: 1,
        },
      }}
    >
      <Paper
        elevation={0}
        onClick={onClick}
        sx={{
          backgroundColor: statusInfo.bg,
          borderRadius: 2,
          padding: '8px 10px',
          display: 'inline-block',
          minWidth: 190,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick
            ? {
                opacity: 0.8,
              }
            : {},
        }}
      >
        <Typography
          variant="caption"
          component="span"
          fontWeight="bold"
          sx={{ color: statusInfo.color, fontSize: '13px', lineHeight: '13px' }}
        >
          {statusInfo.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: statusInfo.color, display: 'block', fontSize: '12px', lineHeight: '12px' }}
        >
          {description || statusInfo.description}
        </Typography>
      </Paper>
      {onClick && (
        <Box
          className="edit-icon"
          onClick={onClick}
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            backgroundColor: 'white',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 1,
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            '& .MuiSvgIcon-root': {
              fontSize: 12,
              color: '#666',
            },
          }}
        >
          <EditIcon />
        </Box>
      )}
    </Box>
  );
};

export default PackageStatus;
